import React, { useState } from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { ChevronDown, Search, MessageCircle, Users, Zap, Shield, User, Globe } from 'lucide-react';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left hover:text-brand-indigo transition-colors group"
      >
        <span className="text-xl md:text-2xl font-semibold text-gray-900 group-hover:text-brand-indigo transition-colors">
          {question}
        </span>
        <ChevronDown size={20} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="pb-6 text-gray-600 leading-relaxed animate-in fade-in slide-in-from-top-1 duration-200">
          {typeof answer === 'string' ? <p className="text-base leading-7">{answer}</p> : answer}
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  return (
    <StaticContentPage title="Frequently Asked Questions">
      <div className="max-w-3xl mb-12">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Welcome to the Chooziq FAQ page.
        </p>
        <p className="text-gray-600 leading-relaxed">
          This page answers common questions about the platform, communities, live sessions, realtime interaction features, and account usage.
        </p>
      </div>

      <div className="space-y-16 mb-16">
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
              <Globe size={20} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">General Questions</h2>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 px-8 py-6 space-y-4">
            <FAQItem 
              question="What is Chooziq?" 
              answer="Chooziq is a realtime interaction and collaborative engagement platform designed for communities, events, workshops, classrooms, webinars, conferences, and organizations. It enables users to host live sessions, create communities, and continue discussions beyond live events." 
            />
            <FAQItem 
              question="What can I do on Chooziq?" 
              answer="Users can create and join communities, host live interactive sessions, participate in live Q&A, collaborate through brainstorming boards, create polls, share resources, and engage with communities over time." 
            />
            <FAQItem 
              question="Is Chooziq free to use?" 
              answer="Certain features may be available for free, while additional tools or organizational features may require subscription plans in the future. Pricing details vary depending on platform offerings." 
            />
            <FAQItem 
              question="Who is Chooziq designed for?" 
              answer="Chooziq is built for universities, schools, conferences, event organizers, tech and professional communities, and any group looking for interactive online engagement." 
            />
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
              <User size={20} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Account and Access</h2>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 px-8">
            <FAQItem 
              question="Do I need an account to use Chooziq?" 
              answer="Some public content may be viewable without an account, but registration is generally required to participate in sessions, join communities, or host events." 
            />
            <FAQItem 
              question="How do I create an account?" 
              answer="Visit Chooziq, select Sign Up, enter your information, and verify your email address to complete your profile setup." 
            />
            <FAQItem 
              question="Can I reset my password?" 
              answer="Yes. Use the 'Forgot Password' option on the login page to receive instructions via email." 
            />
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
              <Users size={20} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Communities</h2>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 px-8">
            <FAQItem 
              question="What are Communities?" 
              answer="Communities are collaborative spaces where users host discussions, organize events, share resources, and create live rooms around shared interests." 
            />
            <FAQItem 
              question="Can communities be private?" 
              answer="Yes. Communities can be public, private, invite-only, or organization-restricted as configured by administrators." 
            />
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
              <Zap size={20} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Live Sessions and Interaction</h2>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 px-8">
            <FAQItem 
              question="What is a Live Room?" 
              answer="A Live Room is a realtime space for Q&A, brainstorming sessions, polls, and audience engagement activities." 
            />
            <FAQItem 
              question="Are sessions saved after they end?" 
              answer="Depending on settings, discussions, questions, and brainstorm boards may remain accessible after the live event ends to allow ongoing interaction." 
            />
            <FAQItem 
              question="Can participants ask anonymous questions?" 
              answer="Yes, some sessions allow anonymous participation if enabled by the moderator." 
            />
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
              <Shield size={20} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Moderation and Safety</h2>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 px-8">
            <FAQItem 
              question="Who moderates communities?" 
              answer="Moderation is handled by community administrators, organizers, or the Chooziq platform team." 
            />
            <FAQItem 
              question="How do I report inappropriate content?" 
              answer="Use the reporting tools available on the platform or contact support for abusive behavior, spam, or harassment." 
            />
          </div>
        </section>
      </div>

      <div className="bg-gray-900 p-10 rounded-3xl text-white text-center">
        <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
        <p className="text-gray-400 mb-8">We're here to help you get the most out of Chooziq.</p>
        <a href="mailto:support@chooziq.com" className="text-xl font-bold text-brand-indigo hover:text-brand-indigo/80 transition-colors">
          support@chooziq.com
        </a>
      </div>
    </StaticContentPage>
  );
};

export default FAQ;
